import axios, { AxiosResponse } from "axios";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  showUIDirectives,
  withUIDirectives,
} from "components/utils/UI_Directive/showUIDirectives";
import { SessionExtend } from "components/utils/SessionCheckUtils";

const beAddress = process.env.REACT_APP_API_URL;

interface HealthMetricsParams {
  [key: string]: string | number | boolean;
}

interface ApiResponse {
  status: string;
  data?: any;
}

export interface UIDirective {
  type: string;
  message: string;
}

export const handleLogout = async () => {
  const sessionUid = localStorage.getItem("session_uid");
  const userUid = localStorage.getItem("user_uid");

  if (!userUid) {
    console.error("User UID is missing.");
    localStorage.clear();
    window.location.replace("/login");
    return;
  }
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/logout`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        },
        credentials: "include",
      }
    );

    if (response.ok) {
      // Clear all localStorage data
      localStorage.clear();
      // Use window.location.replace to prevent going back to authenticated pages
      window.location.replace("/login");
    } else {
      console.error("Logout failed with status:", response.status);
      // Even if logout fails on server, clear local data and redirect
      localStorage.clear();
      window.location.replace("/login");
    }
  } catch (error) {
    console.error("Logout error:", error);
    // Even on error, clear local data and redirect
    localStorage.clear();
    window.location.replace("/login");
  }
};

export const getHealthMetrics = async (
  sessionUid: string,
  userUid: string,
  params: any
) => {
  try {
    const apiResponse = await axios.get(
      `${beAddress}/api/get-individual-metrics`,
      {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
        params,
      }
    );
    SessionExtend();

    return apiResponse.status === 200 ? withUIDirectives(apiResponse.data) : {};
  } catch (error) {
    console.error("Error fetching health metrics:", error);
    return {};
  }
};

export const fetchChallenges = async (
  sessionUid: string | null,
  userUid: string | null,
  params: any
) => {
  try {
    const response = await axios.get(`${beAddress}/api/challenges`, {
      headers: {
        accept: "application/json",
        "user-uid": userUid || "",
      },
      withCredentials: true,
      params: params,
    });

    if (response.status === 200) {
      return withUIDirectives(response.data);
    }
    SessionExtend();
    return {
      status: "Failed",
      message: "Unexpected response from server",
      data: [],
    };
  } catch (error: any) {
    console.error("Error fetching challenges:", error);

    return {
      status: "Failed",
      message:
        error?.response?.data?.message ||
        "Network error: Unable to fetch challenges.",
      data: [],
    };
  }
};

export const addChallenges = async (
  sessionUid: string | null,
  userUid: string | null,
  params: any
) => {
  try {
    const response = await axios.post(`${beAddress}/api/challenges`, params, {
      headers: {
        accept: "application/json",
        "user-uid": userUid || "",
      },
      withCredentials: true,
    });
    SessionExtend();
    return response.status === 200 ? response.data : {};
  } catch (error) {
    console.error("Error adding challenges:", error);
    return {};
  }
};

export const deleteChallenges = async (
  sessionUid: string,
  userUid: string,
  params: any
) => {
  try {
    const response = await axios.delete(`${beAddress}/api/challenges`, {
      headers: {
        accept: "application/json",
        "user-uid": userUid || "",
      },
      withCredentials: true,
      data: params,
    });
    return response.status === 200 ? response.data : {};
  } catch (error) {
    console.error("Error while delete challenge", error);
    return {};
  }
};
export const updateChallenges = async (
  sessionUid: string,
  userUid: string,
  payload: any[]
) => {
  const headers = {
    accept: "application/json",
    "user-uid": userUid,
  };

  const response = await axios.put(`${beAddress}/api/challenges`, payload, {
    headers,
    withCredentials: true,
  });
  return response.data;
};

export const fetchGroups = async (
  sessionUid: string | null,
  userUid: string | null,
  params: any
) => {
  try {
    const response = await axios.get(`${beAddress}/api/groups`, {
      headers: {
        accept: "application/json",
        "user-uid": userUid || "",
      },
      withCredentials: true,
      params: params,
    });
    SessionExtend();
    return response.status === 200 ? withUIDirectives(response.data) : [];
  } catch (error) {
    console.error("Error fetching challenges:", error);
    return [];
  }
};

export const fetchGroupMap = async (
  sessionUid: string | null,
  userUid: string | null,
  params: any
) => {
  try {
    const response = await axios.get(`${beAddress}/api/groupmap`, {
      headers: {
        accept: "application/json",
        "user-uid": userUid || "",
      },
      withCredentials: true,
      params: params,
    });
    SessionExtend();
    return response.status === 200 ? response.data : [];
  } catch (error) {
    console.error("Error fetching challenges:", error);
    return [];
  }
};

// export const fetchGroups = async (
//   sessionUid: string | null,
//   userUid: string | null,
//   params: any
// ) => {
//   try {
//     const response = await axios.get(`${beAddress}/api/groupmap`, {
//       headers: {
//         accept: "application/json",
//         "session-uid": sessionUid || "",
//         "user-uid": userUid || "",
//       },
//       params: params,
//     });

//     return response.status === 200 ? response.data : [];
//   } catch (error) {
//     console.error("Error fetching challenges:", error);
//     return [];
//   }
// };

// export const fetchGroups = async (
//   sessionUid: string | null,
//   userUid: string | null,
//   params: any
// ) => {
//   try {
//     const response = await axios.get(`${beAddress}/api/groupmap`, {
//       headers: {
//         accept: "application/json",
//         "session-uid": sessionUid || "",
//         "user-uid": userUid || "",
//       },
//       params: params,
//     });

//     return response.status === 200 ? response.data : [];
//   } catch (error) {
//     console.error("Error fetching challenges:", error);
//     return [];
//   }
// };

export const assignChallenges = async (
  sessionUid: string | null,
  userUid: string | null,
  params: any
) => {
  try {
    const response = await axios.post(
      `${beAddress}/api/challenges-maping`,
      params,
      {
        headers: {
          accept: "application/json",
          "user-uid": userUid || "",
        },
        withCredentials: true,
      }
    );
    SessionExtend();
    return response.status === 200 ? response.data : {};
  } catch (error) {
    console.error("Error adding challenges:", error);
    return {};
  }
};

export const getAssignedChallenges = async (
  sessionUid: string | null,
  userUid: string | null,
  params: any
) => {
  try {
    const apiResponse = await axios.get(`${beAddress}/api/challenges-maping`, {
      headers: {
        "user-uid": userUid || "",
      },
      withCredentials: true,
      params,
    });
    SessionExtend();
    return apiResponse.status === 200 ? withUIDirectives(apiResponse.data) : {};
  } catch (error) {
    console.error("Error fetching assigned challenges:", error);
    return {};
  }
};

export const useCheckSessionHeartBeat = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      const userUid = localStorage.getItem("user_uid");
      if (!userUid) {
        localStorage.clear();
        navigate("/");
        return;
      }

      try {
        const response = await axios.get(`${beAddress}/api/validate-session`, {
          headers: { "user-uid": userUid },
          withCredentials: true,
        });

        if (response.status === 200 && response.data.status === "Success") {
          localStorage.setItem("lastActiveTime", Date.now().toString());
        } else {
          localStorage.clear();
          navigate("/");
        }
      } catch (error) {
        console.error("Error checking session:", error);
        localStorage.clear();
        navigate("/");
      }
    };

    checkSession();
  }, [navigate]);
};

export const fetchAssignedChallenges = async (
  sessionUid: string | null,
  userUid: string | null,
  params: any
) => {
  try {
    const response = await axios.get(`${beAddress}/api/assigned-challenges`, {
      headers: {
        accept: "application/json",
        "user-uid": userUid || "",
      },
      withCredentials: true,
      params: params,
    });

    
    showUIDirectives(response?.data?.ui_directives || []);
    SessionExtend();
    return response.status === 200 ? response.data : [];
  } catch (error) {
    console.error("Error fetching challenges:", error);
    return [];
  }
};

// Get Started Page API
interface UpdateUserProfilePayload {
  user_uid: string;
  first_name: string;
  last_name: string;
  alt_phone: string;
  user_type: string;
  org_uid: string;
  is_initial_login?: boolean;
  gender?: string;
}
interface UpdateProfileResult {
  success: boolean;
  message: string;
}
export const updateUserProfile = async (
  payload: UpdateUserProfilePayload,
  sessionUid: string
): Promise<UpdateProfileResult> => {
  try {
    SessionExtend();
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/user-profile`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "user-uid": payload.user_uid,
        },
        withCredentials: true,
      }
    );

    if (response.data.status === "Success") {
      return { success: true, message: "" };
    } else {
      return {
        success: false,
        message: response.data.message || "Error updating profile.",
      };
    }
  } catch (error) {
    console.error("updateUserProfile error:", error);
    return {
      success: false,
      message: "Something went wrong. Please try again.",
    };
  }
};
export const fetchUserProfileAPI = async (
  beAddress: string,
  sessionUid: string,
  userUid: string,
  targetUserUid: string
) => {
  try {
    const response = await axios.get(`${beAddress}/api/public-user-profile`, {
      headers: {
        "Content-Type": "application/json",
        "user-uid": userUid,
      },
      withCredentials: true,
      params: {
        target_user_uid: targetUserUid,
      },
    });

    if (
      response.data.status === "Success" &&
      typeof response.data.data === "object"
    ) {
      const userData = response.data.data;

      if (userData.image) {
        const isValidBase64 =
          /^(?:[A-Za-z0-9+/]{4})*?(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
        if (isValidBase64.test(userData.image)) {
          let mimeType = "image/png";
          if (userData.image.startsWith("R0lGODlh")) mimeType = "image/gif";
          else if (userData.image.startsWith("/9j/")) mimeType = "image/jpeg";
          else if (userData.image.startsWith("iVBORw0KGgo"))
            mimeType = "image/png";

          userData.image = `data:${mimeType};base64,${userData.image}`;
        } else {
          userData.image = null;
        }
      } else {
        userData.image = null;
      }

      return userData;
    } else {
      console.error("Invalid data format in API response");
      return null;
    }
  } catch (error) {
    console.error("Error fetching user profile:", error);
    return null;
  }
};

export const validatePassword = async (
  sessionUid: string,
  userUid: string,
  payload: any
) => {
  try {
    const response = await axios.post(
      `${beAddress}/api/validate-password`,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        },
        withCredentials: true,
      }
    );
    return response.status === 200 ? response.data : {};
  } catch (error) {
    console.error("Error while validating password", error);
    return {};
  }
};

export const disableSetup2Fa = async (sessionUid: string, userUid: string) => {
  try {
    const response = await axios.post(
      `${beAddress}/api/disable-2fa-qrcode`,
      {},
      {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
      }
    );
    return response.status === 200 ? response.data : {};
  } catch (error) {
    console.error("Error while disabling 2fa qrcode", error);
    return {};
  }
};

export const fetchTwoFaQrCode = async (sessionUid: string, userUid: string) => {
  try {
    const response = await axios.get(`${beAddress}/api/setup-2fa-qrcode`, {
      headers: {
        "user-uid": userUid,
      },
      withCredentials: true,
    });
    return response.status === 200 ? response.data : {};
  } catch (error) {
    console.error("Error while fteching qr code for 2-fa", error);
    return {};
  }
};

export const verifySetup2Fa = async (
  sessionUid: string,
  userUid: string,
  payload: any
) => {
  try {
    const response = await axios.post(
      `${beAddress}/api/verify-setup-2fa-qrcode`,
      payload,
      {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
      }
    );
    return response.status === 200 ? response.data : {};
  } catch (error) {
    console.error("Error while varifying 2fa qrcode", error);
    return {};
  }
};
